import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { motion } from 'framer-motion';
import SectionTitle from './SectionTitle';

const bgImages = [
  '/DESCUBRIR.avif',
  '/DISENAR.avif',
  '/DESARROLLAR.avif',
];

export default function Workflow() {
  const { t } = useLanguage();

  return (
    <section
      id="workflow"
      data-testid="workflow-section"
      className="relative py-24 md:py-32 px-6 md:px-12"
      style={{ background: '#050505' }}
    >
      {/* MAGIA CSS: Esto elimina el lag del hover al 100% */}
      <style>{`
        @media (min-width: 768px) {
          .workflow-card { flex: 1 1 33.33%; transition: flex 0.5s cubic-bezier(0.25, 0.1, 0.25, 1); }
          .workflow-container:hover .workflow-card { flex: 0.6 1 0%; }
          .workflow-container .workflow-card:hover { flex: 1.8 1 0%; }
        }
        
        .workflow-card .bg-img { transform: scale(1); transition: transform 0.7s ease; }
        .workflow-card:hover .bg-img { transform: scale(1.08); }

        .workflow-card .overlay { opacity: 1; background: rgba(5, 5, 5, 0.78); transition: opacity 0.5s ease; }
        .workflow-card:hover .overlay { opacity: 0.6; }

        .workflow-card .glass-border {
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
          transition: border 0.5s ease, box-shadow 0.5s ease;
        }
        .workflow-card:hover .glass-border {
          border: 1px solid rgba(225, 65, 65, 0.4);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.15), 0 8px 32px rgba(225,65,65,0.15);
        }

        .workflow-card .step-number { color: rgba(255, 255, 255, 0.08); transition: color 0.5s ease; }
        .workflow-card:hover .step-number { color: rgba(225, 65, 65, 0.6); }

        .workflow-card .step-desc { opacity: 1; transition: opacity 0.5s ease; }
        .workflow-container:hover .workflow-card:not(:hover) .step-desc { opacity: 0.5; }
      `}</style>

      <div className="max-w-7xl mx-auto">
        <SectionTitle text={t.workflow.label} direction="right" />

        <div className="workflow-container flex flex-col md:flex-row gap-4 md:gap-5">
          {t.workflow.steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              data-testid={`workflow-step-${i}`}
              className="workflow-card relative min-h-[320px] md:min-h-[420px] rounded-2xl p-8 md:p-10 flex flex-col justify-between overflow-hidden cursor-pointer"
            >
              {/* Background image */}
              <div className="absolute inset-0 overflow-hidden">
                <img
                  src={bgImages[i]}
                  alt=""
                  loading="lazy"
                  className="bg-img w-full h-full object-cover"
                />
              </div>

              {/* Dark overlay */}
              <div className="overlay absolute inset-0" />

              {/* Glass border + shine */}
              <div className="glass-border absolute inset-0 rounded-2xl pointer-events-none" />

              {/* Top light reflection */}
              <div
                className="absolute top-0 left-0 right-0 h-1/3 rounded-t-2xl pointer-events-none"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, transparent 100%)' }}
              />

              {/* Content */}
              <div className="relative z-10">
                <span
                  className="step-number italic"
                  style={{
                    fontFamily: "'Instrument Serif', serif",
                    fontSize: 'clamp(3.5rem, 6vw, 5.5rem)',
                    lineHeight: 1,
                  }}
                >
                  {step.number}
                </span>
              </div>

              <div className="relative z-10">
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3 tracking-tight">
                  {step.title}
                </h3>
                <p className="step-desc font-body text-sm md:text-base text-zinc-400 leading-relaxed max-w-md">
                  {step.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex justify-center mt-10 md:mt-14"
        >
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="workflow-cta-button"
            className="font-body text-sm bg-white text-[#050505] px-8 py-3.5 rounded-full hover:bg-zinc-200 transition-all duration-300 hover:scale-105"
          >
            {t.workflow.cta}
          </button>
        </motion.div>
      </div>
    </section>
  );
}
